import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import MyButton from '../basic/MyButton';
import { ArrowUpCircle, ArrowDownCircle, AlertTriangle, Lightbulb, PlusCircle } from 'lucide-react';
import { fetchCheckingBalanceSheet } from '../../api';
import './ChildModalBalanceSheet.css';

Modal.setAppElement('#root');

const ModalBalanceSheet = ({ isOpen, onClose, childId, accountId, accountName }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [data, setData] = useState(null);

    useEffect(() => {
        if (isOpen && childId) {
            setLoading(true);
            setError('');
            fetchCheckingBalanceSheet(childId)
                .then(response => {
                    setData(response);
                    setLoading(false);
                })
                .catch(err => {
                    console.error('Error fetching balance sheet:', err);
                    setError('Failed to load balance sheet data. Please try again.');
                    setLoading(false);
                });
        }
    }, [isOpen, childId]);

    if (loading) {
        return (
            <Modal
                isOpen={isOpen}
                onRequestClose={onClose}
                contentLabel="Weekly Balance Sheet"
                className="bs-modal"
                overlayClassName="bs-overlay"
            >
                <div className="bs-loading-spinner" />
            </Modal>
        );
    }

    if (error) {
        return (
            <Modal
                isOpen={isOpen}
                onRequestClose={onClose}
                contentLabel="Weekly Balance Sheet"
                className="bs-modal"
                overlayClassName="bs-overlay"
            >
                <div className="bs-error-message">{error}</div>
                <div className="bs-actions">
                    <MyButton onClick={onClose}>Close</MyButton>
                </div>
            </Modal>
        );
    }

    if (!data) return null;

    const isNegative = data.summary.net_change < 0;
    const hasExpenses = data.expenses.items.length > 0;
    const recommendedSplit = Math.ceil((data.summary.total_expenses / (data.income.items[0]?.amount || 0)) * 100 + 2);

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            contentLabel="Weekly Balance Sheet"
            className="bs-modal"
            overlayClassName="bs-overlay"
        >
            <div className="space-y-6 p-4">
                {isNegative && (
                    <div className="warning-alert">
                        <AlertTriangle className="warning-icon" />
                        <p className="warning-text">
                            Weekly expenses (${data.summary.total_expenses.toFixed(2)}) exceed Checking account's income split (${data.summary.total_income.toFixed(2)}). 
                        </p>
                    </div>
                )}

                <div className="balance-sheet-header">
                    <h1>Checking Account - Weekly Balance Sheet</h1>
                    <p>{data.date_range.start_date} - {data.date_range.end_date}</p>
                </div>
                
                <div className="balance-cards">
                    <div className="balance-card income">
                        <div className="card-header">
                            <div className="card-header-title">
                                <ArrowUpCircle className="card-icon" />
                                <h2>Income</h2>
                            </div>
                        </div>
                        <div className="card-content">
                            {data.income.items.map((item, index) => (
                                <div key={index} className="income-item">
                                    <div className="income-row">
                                        <div className="allowance-text">
                                            <span>{item.type}</span>
                                            {item.details && (
                                                <span className="details-text">{item.details}</span>
                                            )}
                                        </div>
                                        <span className="amount">+${item.amount.toFixed(2)}</span>
                                    </div>
                                </div>
                            ))}
                            <div className="total-row">
                                <span>Total</span>
                                <span className="amount">+${data.income.total.toFixed(2)}</span>
                            </div>
                        </div>
                    </div>

                    <div className="balance-card expenses">
                        <div className="card-header">
                            <ArrowDownCircle className="card-icon" />
                            <h2>Expenses</h2>
                        </div>
                        <div className="card-content">
                            {hasExpenses ? (
                                <>
                                    {data.expenses.items.map((expense, index) => (
                                        <div key={index} className="expense-item">
                                            <span>{expense.name}</span>
                                            <span className="amount">-${expense.amount.toFixed(2)}</span>
                                        </div>
                                    ))}
                                    <div className="total-row">
                                        <span>Total</span>
                                        <span className="amount">-${data.expenses.total.toFixed(2)}</span>
                                    </div>
                                </>
                            ) : (
                                <div className="empty-expenses">
                                    <div className="empty-message">
                                        <p>No expenses proceesed for week:</p>
                                        <p>{data.date_range.start_date} - {data.date_range.end_date}</p>
                                        <p>This report represent the previos week ending on Sunday, New Expenses will appear on next week's balance sheet</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="balance-summary">
                    <h2>Account Balance</h2>
                    <div className="summary-content">
                        <div className="summary-row income">
                            <span>Income</span>
                            <span>+${data.summary.total_income.toFixed(2)}</span>
                        </div>
                        <div className="summary-row expenses">
                            <span>Expenses</span>
                            <span>-${data.summary.total_expenses.toFixed(2)}</span>
                        </div>
                        <div className="summary-row total">
                            <span>Net Weekly Change</span>
                            <span className={isNegative ? "negative" : "positive"}>
                                {isNegative ? "-" : "+"}${Math.abs(data.summary.net_change).toFixed(2)}
                            </span>
                        </div>
                    </div>
                </div>

                <div className={`status-message ${isNegative ? "warning" : "info"}`}>
                    <Lightbulb className="message-icon" />
                    <p>
                        {!hasExpenses ? (
                            "No expenses recorded in the report time period, future expenses will be automatically deducted from your Checking account every Sunday night."
                        ) : isNegative ? (
                            `To cover your weekly expenses of $${data.summary.total_expenses.toFixed(2)}, Consider increasing your Checking account split or reducing expenses.`
                        ) : (
                            `Your current income split (${data.analysis.current_split_percentage}%) comfortably covers your weekly expenses with a small buffer. This is a healthy balance, allowing you to manage expenses while still saving for your goals.`
                        )}
                    </p>
                </div>

                <MyButton 
                    text="Close" 
                    onClick={onClose} 
                    backgroundColor="#f0f0f0"
                    color="#333"
                    hoverBackgroundColor="#e0e0e0"
                    className="modal-close-button"
                />
            </div>
        </Modal>
    );
};

export default ModalBalanceSheet;